import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { Box, Dialog, Grid, IconButton, Typography } from '@mui/material'
import { Button, message, Select, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import React, { useCallback, useState } from 'react'

import { Link } from 'react-router-dom'
import CircularLoading from '../../../../../components/CircularProgress'
import DistrubuteChannelTag from '../../../../../components/DistrubuteChannelTag'
import OrderTag from '../../../../../components/OrderTag'
import CrmTag from '../../../../../components/OrderTag/crmTag'
import OverdueTag from '../../../../../components/OrderTag/overdue'
import PrintedTag from '../../../../../components/OrderTag/printed'
import StatusCallTag from '../../../../../components/OrderTag/statusCall'
import TelesaleTag from '../../../../../components/OrderTag/telesale'
import ProductItemDetailTag from '../../../../../components/ProductItemDetailTag'
import { Tag } from '../../../../../components/Tag'
import TrackingProviderImage from '../../../../../components/TrackingProviderImage'
import {
  ACTION_TYPE_TITLES,
  ONLY_DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
} from '../../../../../constants/global'
import PageUrls from '../../../../../constants/page-urls'
import { useUser } from '../../../../../hooks/useUser'
import {
  EOrderStatus,
  EOrderType,
  ESortOrder,
  IOrder,
  IOrderItem,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../../../types/order'
import { IFilterMapped } from '../../../components/OrderFilter'
import FacebookTag from '../../../../../components/OrderTag/facebookTag'
import TrackingTimelineModal from '../../../../../components/Modal/TrackingTimelineModal'
import { FiCopy } from 'react-icons/fi'

interface IOrderTableProps {
  selectedData: IOrder[]
  setSelectedData: (orders: IOrder[]) => void
  isLoading: boolean
  data: IOrder[]
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
  statusOrder: EOrderStatus | string
  hasNext?: boolean
  hasPrev?: boolean
  onNext?: () => void
  onPrev?: () => void
  onPerPageChange?: (perPage: number) => void
  pageIndex?: number
  sortOrder?: ESortOrder
  onSortOrderChange?: (sortOrder: ESortOrder) => void
}

const PER_PAGE_OPTIONS = [10, 15, 20, 30, 50, 100]

const OrderListTable = ({
  selectedData,
  setSelectedData,
  isLoading,
  data,
  filter,
  onFilterChange,
  handlePageChange,
  total,
  statusOrder,
  hasNext = false,
  hasPrev = false,
  onNext,
  onPrev,
  onPerPageChange,
  pageIndex = 1,
  sortOrder = 'desc',
  onSortOrderChange,
}: IOrderTableProps) => {
  const { merchant } = useUser()
  const showSocial = !!merchant?.config?.display?.social

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const orderObjects = data?.reduce<{ [key: string]: IOrder }>((acc, cur) => {
      acc[cur.objectId] = cur
      return acc
    }, {})
    const orders = keys.map((key: string) => {
      return orderObjects[key]
    })
    setSelectedData(orders)
  }

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [order, setOrder] = useState<IOrder | null>(null)
  const [slipUrl, setSlipUrl] = useState<string | null>(null)

  const handleShowTracking = useCallback((order: IOrder) => {
    setIsOpen(true)
    setOrder(order)
  }, [])

  const handleCopyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text ?? '')
    message.success(`คัดลอกแล้ว: ${text ?? ''}`)
  }, [])

  const isAiStatus =
    statusOrder === EOrderStatus.AI && filter.isDuplicate === 'true'

  function generateDistinctColor(index: number, total: number) {
    const hue = Math.floor((360 / total) * index)
    return `hsl(${hue}, 80%, 60%)` // สีสดและมองเห็นแตกต่างชัดเจน
  }

  const orderColorCodeDulicateMapper = data?.reduce(
    (acc, order, index) => {
      const phone = order?.recipient?.phone ?? ''

      // จำนวนทั้งหมด เพื่อนำไปคำนวณกระจายสี
      const total = data.length

      // สร้างสีแบบกระจาย hue ให้ไม่ใกล้กัน
      const color = generateDistinctColor(index, total)

      acc[phone] = color
      return acc
    },
    {} as Record<string, string>,
  )

  const columns: TableColumnsType<IOrder & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'status',
        key: 'status',
        align: 'center' as const,
        render: (status: string, order: IOrder) => {
          const lastTracking =
            order.trackings?.[order.trackings?.length - 1] ??
            ({} as { trackingDate: string })
          return (
            <>
              {order.trackingCode && order.trackingProvider && (
                <Box mb={1}>
                  <TrackingProviderImage
                    trackingProvider={
                      order.trackingProvider as OrderShippingProvider
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      textAlign={'center'}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleShowTracking(order)}
                    >
                      {order.trackingCode}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyText(order?.trackingCode ?? '')}
                    >
                      <FiCopy size={12} />
                    </IconButton>
                  </Box>
                  <Typography fontSize={10}>
                    {order?.trackingUpdatedAt
                      ? dayjs(order?.trackingUpdatedAt).format(
                          ONLY_DATE_TIME_FORMAT,
                        )
                      : lastTracking?.trackingDate
                        ? dayjs(lastTracking.trackingDate).format(
                            ONLY_DATE_TIME_FORMAT,
                          )
                        : ''}
                  </Typography>
                </Box>
              )}
              <Box>
                <OrderTag order={order} status={status as EOrderStatus} />
              </Box>
              {order.providerStatusText && (
                <Box
                  pt={1}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleShowTracking(order)}
                >
                  <Tag color={`success`}>{order.providerStatusText}</Tag>
                </Box>
              )}
            </>
          )
        },
      },
      {
        title: 'เลขออเดอร์',
        dataIndex: 'code',
        key: 'code',
        align: 'center' as const,
        render: (code: string, order: IOrder) => (
          <>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={0.5}
            >
              <Typography
                variant="subtitle1"
                color="text.primary"
                component={Link}
                to={`${PageUrls.Orders.Detail}/${order.objectId}`}
                sx={{ cursor: 'pointer' }}
              >
                {code}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyText(`${order?.code ?? ''}`)}
              >
                <FiCopy size={12} />
              </IconButton>
            </Box>
            <Typography fontSize={11}>
              {order.orderDate
                ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                : ''}
            </Typography>
          </>
        ),
      },
      {
        title: 'ราคา',
        dataIndex: 'total',
        key: 'total',
        align: 'center' as const,
        render: (total: string, order: IOrder) => (
          <Grid
            container
            flexDirection={'column'}
            textAlign={'center'}
            minWidth={120}
          >
            <Grid item>
              <Typography
                variant="h5"
                color={
                  order?.paymentMethod === EOrderType.COD ? 'red' : 'green'
                }
              >
                {total.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
                sx={{ marginTop: 1 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {order?.paymentMethod === EOrderType.COD
                    ? 'เก็บเงินปลายทาง'
                    : 'โอนผ่านธนาคาร'}
                </Typography>
                {order?.paymentMethod !== EOrderType.COD &&
                  order?.paymentUrl && (
                    <IconButton
                      size="small"
                      title="ดูสลิป"
                      onClick={() => setSlipUrl(order.paymentUrl ?? null)}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
              </Box>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {order.items.length} รายการ/{order.quantity} ชิ้น
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'จัดส่ง',
        dataIndex: 'recipient',
        key: 'recipient',
        align: 'center' as const,
        render: (recipient: OrderRecipient) => (
          <Grid
            container
            flexDirection={'column'}
            minWidth={200}
            textAlign={'left'}
          >
            <Grid item>
              <Typography variant="subtitle1">{recipient?.name}</Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {recipient?.address} {recipient?.subDistrict}{' '}
                {recipient?.district} {recipient?.province} {recipient?.zipcode}
              </Typography>
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="subtitle1">{recipient?.phone}</Typography>
                {/* create bullet color code */}
                {isAiStatus && (
                  <Box
                    display="inline-block"
                    width={10}
                    height={10}
                    borderRadius="50%"
                    sx={{
                      backgroundColor:
                        orderColorCodeDulicateMapper[recipient?.phone ?? ''],
                    }}
                  >
                    {''}
                  </Box>
                )}
              </Box>
            </Grid>
            {showSocial && recipient?.social && (
              <Grid item>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  title="โซเชียลของลูกค้า"
                >
                  <AlternateEmailIcon
                    sx={{ fontSize: 14 }}
                    color="disabled"
                  />
                  <Typography variant="subtitle2" color="text.secondary">
                    {recipient.social}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        title: 'สินค้า',
        dataIndex: 'items',
        key: 'items',
        align: 'center' as const,
        render: (items: IOrderItem[]) => (
          <Box minWidth={150}>
            {items?.map((item: IOrderItem, index: number) => {
              return (
                <ProductItemDetailTag
                  key={index}
                  item={{
                    name: item.productName,
                    quantity: item.quantity,
                    isCollection: item.isCollection,
                    url: item.imageUrl,
                    commissionName: item.commissionUsername,
                    color: item.meta?.color,
                    size: item.meta?.size,
                  }}
                />
              )
            })}
          </Box>
        ),
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'note',
        key: 'note',
        width: 200,
        align: 'center' as const,
        render: (note: string, order: IOrder) => {
          return (
            <>
              <DistrubuteChannelTag distribution={order.distribution} />
              <TelesaleTag order={order} />
              <CrmTag order={order} />
              <OverdueTag order={order} />
              <PrintedTag order={order} />
              <StatusCallTag order={order} />
              <FacebookTag order={order} />
              {note ? (
                <Typography variant="subtitle2" textAlign={'left'}>
                  {note}
                </Typography>
              ) : (
                <></>
              )}
            </>
          )
        },
      },
      {
        title: (
          <Box
            onClick={() =>
              onSortOrderChange?.(sortOrder === 'desc' ? 'asc' : 'desc')
            }
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: onSortOrderChange ? 'pointer' : 'default',
              userSelect: 'none',
            }}
          >
            วันที่เพิ่ม
            {sortOrder === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        align: 'center' as const,
        render: (createdAt: string, order: IOrder) => (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle1">{order?.actor}</Typography>
            </Grid>
            <Grid item>
              <Typography
                sx={{ mb: 0, lineHeight: 1.1 }}
                fontSize={12}
                variant="subtitle2"
                color="text.secondary"
              >
                {dayjs(createdAt).format(ONLY_DATE_TIME_FORMAT)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'วันที่แก้ไข',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 120,
        align: 'center' as const,
        render: (updatedAt: string, order: IOrder) => {
          const lastAction =
            order.actions?.[order.actions?.length - 1] ??
            ({} as {
              actor: string
              date: string
              createdAt: string
              action: string
            })
          return (
            <Grid container flexDirection={'column'} textAlign={'center'}>
              <Grid item>
                <Typography variant="subtitle1">
                  {lastAction?.actor ?? ''}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="subtitle2"
                  fontSize={12}
                  sx={{ mb: 1, lineHeight: 1.1 }}
                  color="text.secondary"
                >
                  {dayjs(lastAction?.date ?? lastAction?.createdAt).format(
                    ONLY_DATE_TIME_FORMAT,
                  )}
                </Typography>
              </Grid>
              <Grid item>
                <Tag color="success">
                  {ACTION_TYPE_TITLES?.[lastAction?.action ?? ''] ??
                    lastAction?.action ??
                    'ไม่ระบุ'}
                </Tag>
              </Grid>
            </Grid>
          )
        },
      },
      {
        dataIndex: '',
        key: 'action',
        width: 60,
        align: 'center' as const,
        render: (_: undefined, order: IOrder) => (
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <IconButton
                component={Link}
                to={`${PageUrls.Orders.Detail}/${order.objectId}`}
              >
                <InfoIcon />
              </IconButton>
            </Grid>
          </Grid>
        ),
      },
    ]

  const rowSelection = {
    selectedRowKeys: selectedData.map((order: IOrder) => order.objectId),
    onChange: onSelectChange,
    getCheckboxProps: (order: IOrder) => ({
      disabled: !order.items.length && order.status !== EOrderStatus.AI,
    }),
  }

  // const enableSelectRow = [EOrderStatus.PENDING].includes(
  //   statusOrder as EOrderStatus,
  // )

  const enableSelectRow = ![
    EOrderStatus.CANCLELLED,
    EOrderStatus.RETURNED,
  ].includes(statusOrder as EOrderStatus)

  return (
    <>
      <TrackingTimelineModal
        order={order}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={`ประวัติการจัดส่ง ${order?.trackingCode ?? ''}`}
      />
      <Dialog
        open={!!slipUrl}
        onClose={() => setSlipUrl(null)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            position: 'relative',
            m: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setSlipUrl(null)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          component="img"
          src={slipUrl ?? ''}
          alt="สลิปการโอนเงิน"
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '85vh',
            objectFit: 'contain',
          }}
        />
      </Dialog>
      <CircularLoading isLoading={isLoading} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2">
          {total > 0 ? `${total.toLocaleString()} รายการ · ` : ''}
          หน้า {pageIndex}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Select
            value={filter.perPage ?? 15}
            onChange={(value) => onPerPageChange?.(value)}
            style={{ width: 90 }}
            options={PER_PAGE_OPTIONS.map((v) => ({
              value: v,
              label: `${v} / หน้า`,
            }))}
          />
          <Button disabled={!hasPrev || isLoading} onClick={onPrev}>
            ก่อนหน้า
          </Button>
          <Button
            type="primary"
            disabled={!hasNext || isLoading}
            onClick={onNext}
          >
            ถัดไป
          </Button>
        </Box>
      </Box>
      <Table
        loading={isLoading}
        size={'middle'}
        rowSelection={enableSelectRow ? rowSelection : undefined}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((order: IOrder, index: number) => {
          return {
            ...order,
            key: order.objectId,
            numberRow:
              index + 1 + (pageIndex - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2">
          {total > 0 ? `${total.toLocaleString()} รายการ · ` : ''}
          หน้า {pageIndex}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Select
            value={filter.perPage ?? 15}
            onChange={(value) => onPerPageChange?.(value)}
            style={{ width: 90 }}
            options={PER_PAGE_OPTIONS.map((v) => ({
              value: v,
              label: `${v} / หน้า`,
            }))}
          />
          <Button disabled={!hasPrev || isLoading} onClick={onPrev}>
            ก่อนหน้า
          </Button>
          <Button
            type="primary"
            disabled={!hasNext || isLoading}
            onClick={onNext}
          >
            ถัดไป
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default OrderListTable
